import { useTheme } from '@mui/material';

/**
 * SupportIcon component that displays a support logo with optional spinning animation and percentage
 * @param {Object} props - Component props
 * @param {boolean} props.isSpinning - Controls whether the icon is spinning
 * @param {boolean} props.isWhite - If set, forces white color regardless of theme. If not set, uses white for dark mode and primary for light mode
 * @param {string | number} props.size - Size of the icon. Can be number (pixels) or string with units (e.g., '100%', '2rem', '24pt'). Default: 200
 * @param {number} props.percentage - Optional percentage to display (0-100)
 */
export const SupportIcon = ({ 
    isSpinning = false, 
    isWhite, 
    size = 200,
    percentage
}: { 
    isSpinning?: boolean, 
    isWhite?: boolean,
    size?: string | number,
    percentage?: number
}) => {
    const theme = useTheme();
    const color = isWhite !== undefined
        ? (isWhite ? theme.palette.common.white : theme.palette.primary.main)
        : (theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.primary.main);
    
    // Calculate background opacity based on whether it's a spinner or percentage indicator
    const backgroundOpacity = (isSpinning || percentage !== undefined) ? 0.1 : 0.2;
    
    // Convert size to string with units if it's a number
    const sizeWithUnit = typeof size === 'number' ? `${size}px` : size;

    // Calculate the circle progress path if percentage is provided
    const calculateProgressPath = () => {
        if (percentage === undefined) return "";
        const normalizedPercentage = Math.min(100, Math.max(0, percentage));
        const angle = (normalizedPercentage / 100) * 360;
        const radius = 70;
        const cx = 100;
        const cy = 100;

        if (normalizedPercentage === 100) {
            return `M ${cx + radius} ${cy} A ${radius} ${radius} 0 1 1 ${cx - radius} ${cy} A ${radius} ${radius} 0 1 1 ${cx + radius} ${cy}`;
        }

        const rad = (angle - 90) * Math.PI / 180;
        const x = cx + radius * Math.cos(rad);
        const y = cy + radius * Math.sin(rad);
        const largeArc = angle > 180 ? 1 : 0;

        return `M ${cx} ${cy - radius} A ${radius} ${radius} 0 ${largeArc} 1 ${x} ${y}`;
    };
    
    return (
        <svg
           width={sizeWithUnit}
           height={sizeWithUnit}
           viewBox="0 0 200 200"
           version="1.1"
           id="svg1">
          <defs
             id="defs1">
            {isSpinning && (
              <style>
                {`
                  @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                  }
                  #path1 {
                    transform-origin: 100px 100px;
                    animation: spin 2s linear infinite;
                  }
                `}
              </style>
            )}
          </defs>
          <circle
             cx="100"
             cy="100"
             r="70"
             stroke={color}
             strokeWidth="18"
             fill="none"
             strokeOpacity="0.2"
             id="circle1" />
          {percentage !== undefined ? (
            <path
              d={calculateProgressPath()}
              stroke={color}
              strokeWidth="18"
              fill="none"
              strokeOpacity={(isSpinning || percentage !== undefined) ? "0.7" : "1"}
              id="path1" />
          ) : (
            <path
              d="M 131.77933498176827 37.629543306814256 A 70 70 0 0 1 162.37045669318576 68.22066501823173"
              stroke={color}
              strokeWidth="18"
              fill="none"
              strokeOpacity={(isSpinning || percentage !== undefined) ? "0.7" : "1"}
              id="path1" />
          )}
          <path
             d="m 20.34,114.44 q -3.68,0 -6.24,-1.24 -2.56,-1.28 -4.3599997,-3.28 L 13.74,105.88 q 2.84,3.2 7,3.2 2.24,0 3.32,-0.92 1.08,-0.92 1.08,-2.44 0,-1.16 -0.64,-1.92 -0.64,-0.8 -2.6,-1.08 l -2.76,-0.36 q -4.44,-0.56 -6.52,-2.680001 -2.04,-2.12 -2.04,-5.64 0,-1.88 0.72,-3.44 0.72,-1.56 2.04,-2.68 1.36,-1.12 3.28,-1.72 1.96,-0.64 4.44,-0.64 3.16,0 5.56,1 2.4,1 4.12,2.92 l -4.04,4.08 q -1,-1.16 -2.44,-1.88 -1.4,-0.76 -3.56,-0.76 -2.04,0 -3.04,0.72 -1,0.72 -1,2 0,1.44 0.76,2.08 0.8,0.64 2.56,0.92 l 2.76,0.44 q 4.32,0.68 6.4,2.68 2.08,1.960001 2.08,5.600001 0,2 -0.72,3.68 -0.72,1.68 -2.12,2.88 -1.36,1.2 -3.4,1.88 -2.04,0.64 -4.64,0.64 z M 42.020022,86.039999 V 103.16 q 0,2.96 1.16,4.44 1.16,1.44 3.88,1.44 2.72,0 3.88,-1.44 1.16,-1.48 1.16,-4.44 V 86.039999 h 5.92 V 102.52 q 0,3.08 -0.6,5.36 -0.56,2.24 -1.88,3.72 -1.32,1.44 -3.4,2.16 -2.08,0.68 -5.08,0.68 -3,0 -5.08,-0.68 -2.08,-0.72 -3.4,-2.16 -1.32,-1.48 -1.92,-3.72 -0.56,-2.28 -0.56,-5.36 V 86.039999 Z M 64.020032,113.96 V 86.039999 h 13.2 q 1.96,0 3.52,0.68 1.56,0.64 2.64,1.8 1.12,1.16 1.72,2.84 0.6,1.64 0.6,3.6 0,2 -0.6,3.64 -0.6,1.640001 -1.72,2.800001 -1.08,1.16 -2.64,1.84 -1.56,0.64 -3.52,0.64 h -7.12 v 10.08 z m 6.08,-15.360001 h 6.4 q 1.36,0 2.12,-0.68 0.8,-0.72 0.8,-2.08 v -1.76 q 0,-1.36 -0.8,-2.04 -0.76,-0.72 -2.12,-0.72 h -6.4 z M 90.260037,113.96 V 86.039999 h 13.200003 q 1.96,0 3.52,0.68 1.56,0.64 2.64,1.8 1.12,1.16 1.72,2.84 0.6,1.64 0.6,3.6 0,2 -0.6,3.64 -0.6,1.640001 -1.72,2.800001 -1.08,1.16 -2.64,1.84 -1.56,0.64 -3.52,0.64 h -7.120003 v 10.08 z m 6.08,-15.360001 h 6.400003 q 1.36,0 2.12,-0.68 0.8,-0.72 0.8,-2.08 v -1.76 q 0,-1.36 -0.8,-2.04 -0.76,-0.72 -2.12,-0.72 H 96.340037 Z M 127.70004,114.44 q -2.76,0 -5.04,-0.92 -2.28,-0.92 -3.88,-2.72 -1.6,-1.84 -2.48,-4.52 -0.88,-2.72 -0.88,-6.280001 0,-3.56 0.88,-6.24 0.88,-2.72 2.48,-4.52 1.6,-1.84 3.88,-2.76 2.28,-0.92 5.04,-0.92 2.76,0 5.04,0.92 2.28,0.92 3.88,2.76 1.6,1.8 2.48,4.52 0.88,2.68 0.88,6.24 0,3.560001 -0.88,6.280001 -0.88,2.68 -2.48,4.52 -1.6,1.8 -3.88,2.72 -2.28,0.92 -5.04,0.92 z m 0,-5.4 q 2.76,0 4.28,-1.8 1.56,-1.8 1.56,-5.16 v -4.160001 q 0,-3.36 -1.56,-5.16 -1.52,-1.8 -4.28,-1.8 -2.76,0 -4.32,1.8 -1.52,1.8 -1.52,5.16 V 102.08 q 0,3.36 1.52,5.16 1.56,1.8 4.32,1.8 z m 23.44003,4.92 h -6.08 V 86.039999 h 13.24 q 1.92,0 3.48,0.64 1.56,0.64 2.64,1.84 1.12,1.16 1.72,2.8 0.6,1.64 0.6,3.64 0,2.84 -1.28,4.96 -1.24,2.120001 -3.8,3.080001 l 5.48,10.96 h -6.76 l -4.88,-10.24 h -4.36 z m 6.4,-15.360001 q 1.36,0 2.12,-0.68 0.8,-0.72 0.8,-2.08 v -1.76 q 0,-1.36 -0.8,-2.04 -0.76,-0.72 -2.12,-0.72 h -6.4 v 7.28 z m 25.15999,-7.16 V 113.96 h -6.08 V 91.439999 h -7.56 v -5.4 h 21.2 v 5.4 z"
             id="text1"
             style={{
                fontWeight: "bold",
                fontFamily: "IBM Plex Sans",
                fontSize: "40px",
                fill: color,
                stroke: color
             }}
             aria-label="SUPPORT" />
        </svg>
    );
};

